import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import { IS_LOCAL_DEMO } from "@/components/demo/DemoPackageSwitch";
import {
  ArrowLeft,
  Printer,
  ChevronDown,
  Package,
  StickyNote,
  Clock,
  User,
  AlertTriangle,
  X,
  Send,
  Check,
  Plus,
  Mail,
  Trash2,
  Paperclip,
  FileText,
  Image,
  Upload,
  Download,
  History,
  Phone,
  Truck,
  Calendar,
  Pencil,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LineItem = {
  productName: string;
  sizeCategory: string;
  quantity: number;
  quantityType: string;
  product_illustration_url?: string | null;
  size_category_illustration_url?: string | null;
};

type Destination = {
  address: string;
  city: string;
  state: string;
  zip: string;
};

type Attachment = {
  name: string;
  url: string;
  type: string;
  size: number;
  path: string;
  uploadedAt: string;
};

type Client = {
  id: number;
  name: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  notes: string | null;
  created_at: string;
};

type WorkOrder = {
  id: number;
  wo_number: string;
  date: string;
  product_name: string;
  size_category: string;
  quantity: number;
  quantity_type: string;
  weight_lbs: number | null;
  status: string;
  status_history?: Array<{ status: string; changed_at: string; changed_by: string; note?: string | null }>;
  status_updated_at?: string | null;
  priority: "low" | "normal" | "high" | "urgent";
  order_type: string | null;
  notes: string | null;
  work_order_notes?: string | null;
  custom_notes: string | null;
  mixing_guidelines: string | null;
  needs_transportation: boolean;
  destination: Destination | null;
  preferred_delivery_date: string | null;
  preferred_delivery_time: string | null;
  lines: LineItem[] | null;
  client_id: number | null;
  client_name: string | null;
  attachments: Attachment[];
  created_by: string | null;
  created_at: string;
  updated_at: string | null;
  product_illustration_url?: string | null;
  size_category_illustration_url?: string | null;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: { value: string; label: string; bg: string; text: string }[] = [
  { value: "pending",              label: "Pending",              bg: "bg-gray-100 dark:bg-gray-800",           text: "text-gray-700 dark:text-gray-300" },
  { value: "scheduled",            label: "Scheduled",            bg: "bg-blue-100 dark:bg-blue-900/30",        text: "text-blue-700 dark:text-blue-400" },
  { value: "reschedule_proposed",  label: "Reschedule Proposed",  bg: "bg-sky-100 dark:bg-sky-900/30",          text: "text-sky-700 dark:text-sky-400" },
  { value: "reschedule_requested", label: "Reschedule Requested", bg: "bg-indigo-100 dark:bg-indigo-900/30",    text: "text-indigo-700 dark:text-indigo-400" },
  { value: "preparing",            label: "Preparing",            bg: "bg-amber-100 dark:bg-amber-900/30",      text: "text-amber-700 dark:text-amber-400" },
  { value: "ready_for_pickup",     label: "Ready for Pickup",     bg: "bg-purple-100 dark:bg-purple-900/30",    text: "text-purple-700 dark:text-purple-400" },
  { value: "out_for_delivery",     label: "Out for Delivery",     bg: "bg-indigo-100 dark:bg-indigo-900/30",    text: "text-indigo-700 dark:text-indigo-400" },
  { value: "delivered",            label: "Delivered",            bg: "bg-green-100 dark:bg-green-900/30",      text: "text-green-700 dark:text-green-400" },
  { value: "on_hold",              label: "On Hold",              bg: "bg-red-100 dark:bg-red-900/30",          text: "text-red-700 dark:text-red-400" },
  { value: "cancelled",            label: "Cancelled",            bg: "bg-gray-100 dark:bg-gray-800",           text: "text-gray-500 dark:text-gray-500" },
  // Legacy
  { value: "acknowledged",         label: "Acknowledged",         bg: "bg-blue-100 dark:bg-blue-900/30",        text: "text-blue-700 dark:text-blue-400" },
  { value: "blending",             label: "Blend in Progress",    bg: "bg-amber-100 dark:bg-amber-900/30",      text: "text-amber-700 dark:text-amber-400" },
  { value: "blend_complete",       label: "Blend Complete",       bg: "bg-emerald-100 dark:bg-emerald-900/30",  text: "text-emerald-700 dark:text-emerald-400" },
  { value: "awaiting_pickup",      label: "Awaiting Pickup",      bg: "bg-purple-100 dark:bg-purple-900/30",    text: "text-purple-700 dark:text-purple-400" },
  { value: "completed",            label: "Completed",            bg: "bg-green-100 dark:bg-green-900/30",      text: "text-green-700 dark:text-green-400" },
  { value: "in_progress",          label: "In Progress",          bg: "bg-orange-100 dark:bg-orange-900/30",    text: "text-orange-700 dark:text-orange-400" },
];

const PRIORITY_CONFIG: Record<
  WorkOrder["priority"],
  { label: string; bg: string; text: string; hidden?: boolean }
> = {
  low: {
    label: "Low",
    bg: "bg-gray-100 dark:bg-gray-800",
    text: "text-gray-600 dark:text-gray-400",
  },
  normal: { label: "Normal", bg: "", text: "", hidden: true },
  high: {
    label: "High",
    bg: "bg-orange-100 dark:bg-orange-900/30",
    text: "text-orange-700 dark:text-orange-400",
  },
  urgent: {
    label: "Urgent",
    bg: "bg-red-100 dark:bg-red-900/30",
    text: "text-red-700 dark:text-red-400",
  },
};

const SIZE_LABELS: Record<string, string> = {
  "9lb": "9 lb",
  "7.5qt": "7.5 qt",
  "1cf": "1 cu ft",
  "1.5cf": "1.5 cu ft",
  "2cf": "2 cu ft",
  tote: "Tote",
  bulk: "Bulk",
  truckload: "Truckload",
  other: "Other",
};

function formatDate(dateStr: string | null) {
  if (!dateStr) return "--";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(dateStr: string | null) {
  if (!dateStr) return "--";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatWeight(lbs: number | null) {
  if (lbs === null || lbs === undefined) return "--";
  return lbs.toLocaleString("en-US") + " lbs";
}

function sizeLabel(cat: string) {
  return SIZE_LABELS[cat] || cat;
}

// ---------------------------------------------------------------------------
// Status Stepper (mirrors the mobile public view)
// ---------------------------------------------------------------------------

const FLOW_STAGES: { value: string; label: string; color: string }[] = [
  { value: "pending",          label: "Pending",           color: "#6b7280" },
  { value: "scheduled",        label: "Scheduled",         color: "#3b82f6" },
  { value: "preparing",        label: "Preparing",         color: "#f59e0b" },
  { value: "ready_for_pickup", label: "Ready / Pickup",    color: "#8b5cf6" },
  { value: "out_for_delivery", label: "Out for Delivery",  color: "#2563eb" },
  { value: "delivered",        label: "Delivered",         color: "#059669" },
];

const FLOW_STATUS_ALIASES: Record<string, string> = {
  acknowledged: "scheduled",
  reschedule_proposed: "scheduled",
  reschedule_requested: "pending",
  blending: "preparing",
  blend_complete: "preparing",
  in_progress: "preparing",
  awaiting_pickup: "ready_for_pickup",
  completed: "delivered",
};

function formatRel(iso: string | null | undefined) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const diff = Date.now() - t;
  const s = Math.max(0, Math.floor(diff / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function StatusStepper({ workOrder }: { workOrder: WorkOrder | null }) {
  if (!workOrder) return null;
  const history = Array.isArray(workOrder.status_history) ? workOrder.status_history : [];
  const historyByStatus = new Map(history.map((h) => [h.status, h]));
  const rawCurrent = workOrder.status || "pending";
  const current = FLOW_STATUS_ALIASES[rawCurrent] || rawCurrent;
  const currentIdx = FLOW_STAGES.findIndex((s) => s.value === current);
  const isSide = rawCurrent === "on_hold" || rawCurrent === "cancelled" || rawCurrent === "reschedule_proposed" || rawCurrent === "reschedule_requested";

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-foreground uppercase tracking-wide">Progress</h3>
        {isSide && (
          <span className="px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
            {STATUS_OPTIONS.find((s) => s.value === rawCurrent)?.label || rawCurrent}
          </span>
        )}
      </div>

      {/* Horizontal steps (desktop) / vertical (mobile) */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-0 sm:gap-0">
        {FLOW_STAGES.map((stage, idx) => {
          const h = historyByStatus.get(stage.value) || history.find((x) => (FLOW_STATUS_ALIASES[x.status] || x.status) === stage.value);
          const isDone = !!h || (currentIdx >= 0 && idx < currentIdx);
          const isCurrent = stage.value === current;
          const isFuture = !isDone && !isCurrent;
          const isLast = idx === FLOW_STAGES.length - 1;
          return (
            <div key={stage.value} className="flex sm:flex-col sm:flex-1 sm:items-center sm:text-center gap-3 sm:gap-0 mb-3 sm:mb-0 relative">
              {/* Connector — hide on last */}
              {!isLast && (
                <div
                  className={`hidden sm:block absolute top-[11px] left-1/2 right-[-50%] h-0.5 z-0 ${isDone ? "" : "bg-border"}`}
                  style={{ background: isDone ? stage.color : undefined }}
                />
              )}
              <div className="flex flex-col sm:flex-col sm:items-center items-start z-10">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 relative sm:mb-2 ${
                    isCurrent ? "ring-4 ring-offset-0" : ""
                  }`}
                  style={{
                    background: isDone || isCurrent ? stage.color : "#e4ebe4",
                    ...(isCurrent ? { boxShadow: `0 0 0 4px ${stage.color}33` } : {}),
                  }}
                >
                  {isDone && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <div className="flex-1 sm:flex-none min-w-0 sm:min-w-[60px] max-w-[140px] ml-3 sm:ml-0">
                  <div className={`text-xs font-semibold leading-tight ${isFuture ? "text-muted-foreground" : "text-foreground"}`}>
                    {stage.label}
                    {isCurrent && <span className="ml-1 text-[9px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">now</span>}
                  </div>
                  {h ? (
                    <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
                      {formatRel(h.changed_at)}
                      <br />
                      <span className="opacity-80">{h.changed_by}</span>
                    </div>
                  ) : (
                    <div className="text-[10px] text-muted-foreground/50 mt-0.5">—</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Full history list (collapsible) */}
      {history.length > 1 && (
        <details className="mt-4 pt-4 border-t border-border">
          <summary className="text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground">
            Full history ({history.length})
          </summary>
          <div className="mt-3 space-y-2">
            {[...history].reverse().map((h, i) => {
              const stageMeta = FLOW_STAGES.find((s) => s.value === h.status);
              return (
                <div key={i} className="flex items-start gap-3 text-xs">
                  <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: stageMeta?.color || "#6b7280" }} />
                  <div className="flex-1">
                    <div className="font-medium" style={{ color: stageMeta?.color || undefined }}>
                      {stageMeta?.label || h.status}
                    </div>
                    <div className="text-muted-foreground text-[11px]">
                      {formatRel(h.changed_at)} &middot; {h.changed_by}
                      {h.note && <span className="text-amber-700 dark:text-amber-400"> &middot; &ldquo;{h.note}&rdquo;</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </details>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ViewWorkOrder() {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [notifySuccess, setNotifySuccess] = useState<string | null>(null);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [teamRecipients, setTeamRecipients] = useState<{ id: number; name: string; email: string; checked: boolean }[]>([]);
  const [extraEmails, setExtraEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recentRecipients, setRecentRecipients] = useState<string[]>([]);
  const [notifyComment, setNotifyComment] = useState("");
  const [uploading, setUploading] = useState(false);
  const [linkedBols, setLinkedBols] = useState<any[]>([]);
  const [loadingBols, setLoadingBols] = useState(false);

  // Link BOL picker
  const [showBolPicker, setShowBolPicker] = useState(false);
  const [bolSearch, setBolSearch] = useState("");
  const [availableBols, setAvailableBols] = useState<any[]>([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);

  // Link Client picker
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);

  // Inline client contact editor
  const [showContactEditor, setShowContactEditor] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [contactForm, setContactForm] = useState<{
    contact_name: string;
    contact_email: string;
    contact_phone: string;
    address: string;
    notes: string;
  }>({
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    notes: "",
  });

  // Editable notes
  const [editableNotes, setEditableNotes] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const notesSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Transportation & delivery (editable)
  const [showDeliveryEditor, setShowDeliveryEditor] = useState(false);
  const [deliverySaving, setDeliverySaving] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({
    needsTransportation: false,
    address: "",
    city: "",
    state: "",
    zip: "",
    preferredDate: "",
    preferredTime: "",
  });

  // Activity log
  type ActivityEntry = {
    id: number;
    field_name: string;
    old_value: string | null;
    new_value: string | null;
    changed_by: string;
    changed_by_email: string | null;
    changed_at: string;
  };
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  // -----------------------------------------------------------------------
  // Fetch
  // -----------------------------------------------------------------------

  const fetchWorkOrder = async (silent = false) => {
    if (!silent) setLoading(true);
    if (!silent) setError(null);
    try {
      const res = await apiFetch(`/api/ops/work-orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load work order (${res.status})`);
      const data = await res.json();
      setWorkOrder(data.workOrder ?? data.work_order ?? data);
    } catch (err: any) {
      if (!silent) setError(err?.message || "Failed to load work order");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchWorkOrder();
  }, [id, token]);

  // Real-time polling — silent refresh every 15s
  useEffect(() => {
    if (!id || !token) return;
    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") fetchWorkOrder(true);
    }, 15000);
    return () => clearInterval(intervalId);
  }, [id, token]);

  // Initialize editable notes and delivery form when work order loads
  useEffect(() => {
    if (workOrder) {
      const main = workOrder.notes || workOrder.work_order_notes || "";
      const legacy = workOrder.custom_notes || "";
      // Merge legacy custom_notes into the single notes field
      setEditableNotes(legacy ? (main ? `${main}\n\n${legacy}` : legacy) : main);
      const dest = workOrder.destination;
      setDeliveryForm({
        needsTransportation: !!workOrder.needs_transportation,
        address: dest?.address || "",
        city: dest?.city || "",
        state: dest?.state || "",
        zip: dest?.zip || "",
        preferredDate: workOrder.preferred_delivery_date ? workOrder.preferred_delivery_date.slice(0, 10) : "",
        preferredTime: workOrder.preferred_delivery_time || "",
      });
    }
  }, [workOrder?.id]);

  // Debounced save for notes
  const debounceSaveNotes = (notes: string) => {
    if (notesSaveTimer.current) clearTimeout(notesSaveTimer.current);
    notesSaveTimer.current = setTimeout(async () => {
      setNotesSaving(true);
      setNotesSaved(false);
      try {
        // Always clear legacy customNotes so merged content lives only in workOrderNotes
        const res = await apiFetch(`/api/ops/work-orders/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ workOrderNotes: notes, customNotes: "" }),
        });
        if (res.ok) {
          setNotesSaved(true);
          setTimeout(() => setNotesSaved(false), 2000);
        }
      } catch { /* silent */ }
      finally { setNotesSaving(false); }
    }, 1000);
  };

  // Fetch linked BOLs
  useEffect(() => {
    if (!id || !token) return;
    setLoadingBols(true);
    apiFetch(`/api/ops/work-orders/${id}/bols`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setLinkedBols(data || []))
      .catch(() => setLinkedBols([]))
      .finally(() => setLoadingBols(false));
  }, [id, token]);

  // Fetch activity log
  const fetchActivity = () => {
    if (!id || !token) return;
    setLoadingActivity(true);
    apiFetch(`/api/ops/work-orders/${id}/activity`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setActivity(data || []))
      .catch(() => setActivity([]))
      .finally(() => setLoadingActivity(false));
  };

  useEffect(() => {
    fetchActivity();
  }, [id, token]);

  // -----------------------------------------------------------------------
  // Link / Unlink BOLs
  // -----------------------------------------------------------------------

  const fetchAvailableBols = async () => {
    setLoadingAvailable(true);
    try {
      const res = await apiFetch("/api/ops/bols", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const bols = data.bols ?? data ?? [];
      // Exclude BOLs already linked to THIS work order
      const linkedIds = new Set(linkedBols.map((b: any) => b.id));
      setAvailableBols(bols.filter((b: any) => !linkedIds.has(b.id)));
    } catch {}
    setLoadingAvailable(false);
  };

  const openBolPicker = () => {
    setShowBolPicker(true);
    setBolSearch("");
    fetchAvailableBols();
  };

  const linkBol = async (bolId: number) => {
    try {
      await apiFetch(`/api/ops/bols/${bolId}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ workOrderId: Number(id) }),
      });
      // Refresh linked BOLs
      const res = await apiFetch(`/api/ops/work-orders/${id}/bols`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setLinkedBols(await res.json());
      // Remove from available
      setAvailableBols((prev) => prev.filter((b) => b.id !== bolId));
    } catch {}
  };

  const unlinkBol = async (bolId: number) => {
    try {
      await apiFetch(`/api/ops/bols/${bolId}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ workOrderId: null }),
      });
      setLinkedBols((prev) => prev.filter((b) => b.id !== bolId));
    } catch {}
  };

  // -----------------------------------------------------------------------
  // Link / Unlink Client
  // -----------------------------------------------------------------------

  const fetchAvailableClients = async () => {
    setLoadingClients(true);
    try {
      const res = await apiFetch("/api/ops/clients", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const clients = (data.clients ?? data ?? []) as Client[];
      setAvailableClients(clients);
    } catch {}
    setLoadingClients(false);
  };

  const openClientPicker = () => {
    setShowClientPicker(true);
    setClientSearch("");
    fetchAvailableClients();
  };

  const linkClient = async (client: Client) => {
    if (!workOrder) return;
    try {
      const res = await apiFetch(`/api/ops/work-orders/${id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: client.id, clientName: client.name }),
      });
      if (res.ok) {
        setWorkOrder({ ...workOrder, client_id: client.id, client_name: client.name });
        fetchActivity();
      }
      setShowClientPicker(false);
    } catch {}
  };

  const unlinkClient = async () => {
    if (!workOrder) return;
    try {
      const res = await apiFetch(`/api/ops/work-orders/${id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: null, clientName: null }),
      });
      if (res.ok) {
        setWorkOrder({ ...workOrder, client_id: null, client_name: null });
        fetchActivity();
        setShowContactEditor(false);
      }
    } catch {}
  };

  // -----------------------------------------------------------------------
  // Status update
  // -----------------------------------------------------------------------

  const handleStatusChange = async (newStatus: WorkOrder["status"]) => {
    if (!workOrder) return;
    setUpdatingStatus(true);
    try {
      const res = await apiFetch(`/api/ops/work-orders/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error("Failed to update status");
      setWorkOrder({ ...workOrder, status: newStatus });
      fetchActivity(); // Refresh activity log
    } catch (err: any) {
      setError(err?.message || "Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const runFulfillmentAction = async (action: string, extra: Record<string, unknown> = {}) => {
    if (!workOrder) return;
    setUpdatingStatus(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/ops/work-orders/${id}/fulfillment-action`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action, ...extra }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Action failed");
      if (data.work_order) setWorkOrder({ ...workOrder, ...data.work_order });
      else setWorkOrder({ ...workOrder, status: action === "ready_for_pickup" ? "ready_for_pickup" : action === "out_for_delivery" ? "out_for_delivery" : action === "delivered" ? "delivered" : action });
      fetchActivity();
    } catch (err: any) {
      setError(err?.message || "Action failed");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const saveDelivery = async () => {
    if (!workOrder) return;
    setDeliverySaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/ops/work-orders/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          needsTransportation: deliveryForm.needsTransportation,
          destinationAddress: deliveryForm.address.trim() || null,
          destinationCity: deliveryForm.city.trim() || null,
          destinationState: deliveryForm.state.trim() || null,
          destinationZip: deliveryForm.zip.trim() || null,
          preferredDeliveryDate: deliveryForm.preferredDate || null,
          preferredDeliveryTime: deliveryForm.preferredTime.trim() || null,
        }),
      });
      if (!res.ok) throw new Error("Failed to update delivery details");
      const dest = {
        address: deliveryForm.address.trim(),
        city: deliveryForm.city.trim(),
        state: deliveryForm.state.trim(),
        zip: deliveryForm.zip.trim(),
      };
      setWorkOrder({
        ...workOrder,
        needs_transportation: deliveryForm.needsTransportation,
        destination: deliveryForm.address.trim() ? dest : null,
        preferred_delivery_date: deliveryForm.preferredDate || null,
        preferred_delivery_time: deliveryForm.preferredTime.trim() || null,
      });
      setShowDeliveryEditor(false);
      fetchActivity();
    } catch (err: any) {
      setError(err?.message || "Failed to save delivery details");
    } finally {
      setDeliverySaving(false);
    }
  };

  // -----------------------------------------------------------------------
  // PDF
  // -----------------------------------------------------------------------

  const handlePrintPdf = () => {
    window.open(`/api/ops/work-orders/${id}/pdf?token=${token}`, "_blank");
  };

  // -----------------------------------------------------------------------
  // Notify Operations
  // -----------------------------------------------------------------------

  const RECENT_KEY = "wo-notify-recent-recipients";

  const loadRecentRecipients = (): string[] => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    } catch { return []; }
  };

  const saveRecentRecipients = (emails: string[]) => {
    // Keep unique, max 20, most recent first
    const unique = [...new Set(emails.map((e) => e.trim().toLowerCase()))].slice(0, 20);
    localStorage.setItem(RECENT_KEY, JSON.stringify(unique));
    setRecentRecipients(unique);
  };

  const removeRecentRecipient = (email: string) => {
    const updated = recentRecipients.filter((e) => e !== email);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
    setRecentRecipients(updated);
  };

  const openNotifyModal = async () => {
    setShowNotifyModal(true);
    setLoadingRecipients(true);
    setExtraEmails([]);
    setNewEmail("");
    setNotifyComment("");
    setRecentRecipients(loadRecentRecipients());
    try {
      const res = await apiFetch("/api/team", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const members = (data.members || data || []).map((m: any) => ({
          id: m.id,
          name: m.name || m.email,
          email: m.email,
          checked: (m.notifications || []).includes("new_work_order"),
        }));
        setTeamRecipients(members);
      }
    } catch {
      // If team fetch fails, still allow adding external emails
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleAddExtraEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !extraEmails.includes(email)) {
      setExtraEmails((prev) => [...prev, email]);
      setNewEmail("");
    }
  };

  const handleSendNotifications = async () => {
    if (notifying) return;
    const selectedTeam = teamRecipients.filter((r) => r.checked).map((r) => r.email);
    const allRecipients = [...selectedTeam, ...extraEmails];
    if (allRecipients.length === 0) {
      setError("Select at least one recipient");
      return;
    }
    setNotifying(true);
    setError(null);
    setNotifySuccess(null);
    try {
      const res = await apiFetch(`/api/ops/work-orders/${id}/notify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ teamEmails: selectedTeam, extraEmails, comment: notifyComment || undefined }),
      });
      if (!res.ok) { const errData = await res.json().catch(() => ({})); throw new Error(errData.message || "Failed to notify operations"); }
      const data = await res.json();
      setNotifySuccess(`Sent to ${data.sent} recipient${data.sent !== 1 ? "s" : ""}`);
      // Save all sent recipients to recent list
      const prev = loadRecentRecipients();
      saveRecentRecipients([...allRecipients, ...prev]);
      setShowNotifyModal(false);
      setTimeout(() => setNotifySuccess(null), 5000);
    } catch (err: any) {
      setError(err?.message || "Failed to send notifications");
    } finally {
      setNotifying(false);
    }
  };

  // -----------------------------------------------------------------------
  // File Upload
  // -----------------------------------------------------------------------

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !workOrder) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await apiFetch(`/api/ops/work-orders/${id}/attachments`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || "Upload failed");
        }
        const attachment = await res.json();
        setWorkOrder((prev) =>
          prev ? { ...prev, attachments: [...(prev.attachments || []), attachment] } : prev
        );
      }
    } catch (err: any) {
      setError(err?.message || "Failed to upload file");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteAttachment = async (filePath: string) => {
    if (!workOrder) return;
    try {
      const res = await apiFetch(`/api/ops/work-orders/${id}/attachments`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ path: filePath }),
      });
      if (!res.ok) throw new Error("Failed to delete");
      setWorkOrder((prev) =>
        prev
          ? { ...prev, attachments: (prev.attachments || []).filter((a) => a.path !== filePath) }
          : prev
      );
    } catch (err: any) {
      setError(err?.message || "Failed to delete attachment");
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const isImageType = (type: string) => type.startsWith("image/");

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (loading) {
    return (
      <div className="space-y-6 max-w-7xl">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 bg-muted rounded-lg animate-pulse" />
          <div className="space-y-2">
            <div className="h-7 w-48 bg-muted rounded animate-pulse" />
            <div className="h-4 w-32 bg-muted rounded animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-card border border-border rounded-xl h-[600px] animate-pulse" />
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-card border border-border rounded-xl p-5 animate-pulse"
              >
                <div className="h-4 w-24 bg-muted rounded mb-3" />
                <div className="h-5 w-40 bg-muted rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !workOrder) {
    return (
      <div className="max-w-7xl space-y-6">
        <button
          onClick={() => navigate("/app/ops/work-orders")}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Work Orders
        </button>
        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-xl p-8 text-center">
          <AlertTriangle
            size={32}
            className="mx-auto text-red-400 mb-3"
          />
          <p className="text-sm text-red-700 dark:text-red-400">
            {error || "Work order not found."}
          </p>
        </div>
      </div>
    );
  }

  const statusCfg = STATUS_OPTIONS.find((s) => s.value === workOrder.status) || STATUS_OPTIONS[0];
  const priorityCfg = PRIORITY_CONFIG[workOrder.priority];
  const hasMultipleLines = workOrder.lines && workOrder.lines.length > 1;

  return (
    <div className="space-y-6 max-w-7xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/app/ops/work-orders")}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="heading-display text-2xl text-foreground font-mono">
                {workOrder.wo_number?.replace(/^WO-/, '#') || workOrder.wo_number}
              </h1>
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${statusCfg.bg} ${statusCfg.text}`}
              >
                {statusCfg.label}
              </span>
              {!priorityCfg.hidden && (
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${priorityCfg.bg} ${priorityCfg.text}`}
                >
                  {priorityCfg.label}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Created {formatDateTime(workOrder.created_at)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!IS_LOCAL_DEMO && <button
            onClick={openNotifyModal}
            disabled={notifying}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition-colors shadow-sm ${
              notifySuccess
                ? "bg-green-600 text-white shadow-green-600/20"
                : "bg-amber-600 hover:bg-amber-700 text-white shadow-amber-600/20"
            } disabled:opacity-50`}
          >
            {notifySuccess ? (
              <>
                <Check size={15} />
                {notifySuccess}
              </>
            ) : (
              <>
                <Send size={15} />
                Notify Operations
              </>
            )}
          </button>}
          <button
            onClick={handlePrintPdf}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-forest-600/20"
          >
            <Printer size={15} />
            Print PDF
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle size={16} className="text-red-600 dark:text-red-400 shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-600"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Progress stepper */}
      <StatusStepper workOrder={workOrder} />

      {/* Primary fulfillment actions */}
      {!IS_LOCAL_DEMO && !["pending", "cancelled", "delivered", "completed"].includes(workOrder.status) && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Fulfillment actions</h3>
          <p className="text-xs text-muted-foreground">Customer is notified when you trigger these.</p>
          <div className="flex flex-wrap gap-2">
            {["scheduled", "acknowledged", "reschedule_proposed"].includes(workOrder.status) && (
              <button
                disabled={updatingStatus}
                onClick={() => runFulfillmentAction("preparing")}
                className="px-3 py-2 text-sm font-semibold rounded-lg bg-amber-600 text-white disabled:opacity-50"
              >
                Start preparing
              </button>
            )}
            {["scheduled", "preparing", "blending", "blend_complete", "acknowledged", "in_progress"].includes(workOrder.status) && (
              <button
                disabled={updatingStatus}
                onClick={() => runFulfillmentAction("ready_for_pickup")}
                className="px-3 py-2 text-sm font-semibold rounded-lg bg-purple-600 text-white disabled:opacity-50"
              >
                Ready for pickup
              </button>
            )}
            {["scheduled", "preparing", "blending", "blend_complete", "ready_for_pickup", "awaiting_pickup", "acknowledged", "in_progress"].includes(workOrder.status) && (
              <>
                <button
                  disabled={updatingStatus}
                  onClick={() => runFulfillmentAction("out_for_delivery", { delivery_eta: "30_min" })}
                  className="px-3 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white disabled:opacity-50"
                >
                  Out for delivery · 30 min
                </button>
                <button
                  disabled={updatingStatus}
                  onClick={() => runFulfillmentAction("out_for_delivery", { delivery_eta: "1_hour" })}
                  className="px-3 py-2 text-sm font-semibold rounded-lg bg-indigo-500 text-white disabled:opacity-50"
                >
                  Out for delivery · 1 hr
                </button>
                <button
                  disabled={updatingStatus}
                  onClick={() => runFulfillmentAction("out_for_delivery", { delivery_eta: "arriving_soon" })}
                  className="px-3 py-2 text-sm font-semibold rounded-lg border border-indigo-300 text-indigo-700 dark:text-indigo-300 disabled:opacity-50"
                >
                  Arriving soon
                </button>
              </>
            )}
            {["out_for_delivery", "ready_for_pickup", "awaiting_pickup"].includes(workOrder.status) && (
              <button
                disabled={updatingStatus}
                onClick={() => runFulfillmentAction("delivered")}
                className="px-3 py-2 text-sm font-semibold rounded-lg bg-emerald-600 text-white disabled:opacity-50"
              >
                Mark delivered
              </button>
            )}
          </div>
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: PDF preview */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-3 border-b border-border bg-muted/30">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              PDF Preview
            </p>
          </div>
          <div className="aspect-[8.5/11] bg-gray-100 dark:bg-gray-900">
            <iframe
              src={`/api/ops/work-orders/${id}/pdf?token=${token}`}
              title="Work Order PDF"
              className="w-full h-full border-0"
            />
          </div>
        </div>

        {/* Right: Details */}
        <div className="space-y-5">
          {/* Status update (secondary / admin) */}
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Clock size={14} className="text-forest-600" />
                Status (admin)
              </h3>
            </div>
            <div className="mt-3 relative">
              <select
                value={workOrder.status}
                onChange={(e) =>
                  handleStatusChange(e.target.value as WorkOrder["status"])
                }
                disabled={updatingStatus}
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground appearance-none pr-8 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500 disabled:opacity-50"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
              />
              {updatingStatus && (
                <p className="text-xs text-muted-foreground mt-1">Updating...</p>
              )}
            </div>
          </div>

          {/* Activity Log — right below status; first line is "Created by ..." */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <History size={14} className="text-forest-600" />
              Activity Log
            </h3>
            {loadingActivity ? (
              <p className="text-xs text-muted-foreground">Loading...</p>
            ) : (
              <div className="space-y-0 divide-y divide-border/60">
                {/* Creation as first entry for continuity */}
                {(workOrder.created_by || workOrder.created_at) && (
                  <div className="py-2.5 first:pt-0">
                    <p className="text-xs text-foreground">
                      {workOrder.created_by ? (
                        <>
                          <span className="font-semibold">Created by {workOrder.created_by}</span>
                          {workOrder.created_at && (
                            <span className="text-muted-foreground"> on {formatDateTime(workOrder.created_at)}</span>
                          )}
                        </>
                      ) : (
                        workOrder.created_at && (
                          <span className="font-semibold">Created on {formatDateTime(workOrder.created_at)}</span>
                        )
                      )}
                    </p>
                  </div>
                )}
                {activity.map((entry) => {
                  const fieldLabel = entry.field_name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                  const time = new Date(entry.changed_at);
                  const timeStr = time.toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  });
                  return (
                    <div key={entry.id} className="py-2.5 last:pb-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-foreground">
                            <span className="font-semibold">{entry.changed_by}</span>
                            {" changed "}
                            <span className="font-medium text-forest-600 dark:text-forest-400">{fieldLabel}</span>
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground">
                            {entry.old_value && (
                              <>
                                <span className="line-through">{entry.old_value}</span>
                                <span>→</span>
                              </>
                            )}
                            <span className="font-medium text-foreground">{entry.new_value || "—"}</span>
                          </div>
                        </div>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">{timeStr}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {!loadingActivity && activity.length === 0 && !workOrder.created_by && !workOrder.created_at && (
              <p className="text-xs text-muted-foreground">No activity yet.</p>
            )}
          </div>

          {/* Client & Delivery */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <User size={14} className="text-forest-600" />
                Client & Delivery
              </h3>
              <div className="flex items-center gap-2">
                {workOrder.client_name ? (
                  <>
                    <button
                      onClick={() => navigate("/app/ops/clients")}
                      className="px-2.5 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
                      title="Open Clients"
                    >
                      View Clients
                    </button>
                    {workOrder.client_id && (
                      <button
                        onClick={async () => {
                          if (!workOrder.client_id) return;
                          setShowContactEditor(true);
                          setContactError(null);
                          setContactLoading(true);
                          try {
                            const res = await apiFetch(`/api/ops/clients/${workOrder.client_id}`, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            if (!res.ok) throw new Error("Failed to load client");
                            const client: Client = await res.json();
                            setContactForm({
                              contact_name: client.contact_name || "",
                              contact_email: client.contact_email || "",
                              contact_phone: client.contact_phone || "",
                              address: client.address || "",
                              notes: client.notes || "",
                            });
                          } catch (err: any) {
                            setContactError(err?.message || "Failed to load client");
                          } finally {
                            setContactLoading(false);
                          }
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-forest-700 dark:text-forest-400 bg-forest-50 dark:bg-forest-900/20 hover:bg-forest-100 dark:hover:bg-forest-900/40 rounded-lg transition-colors"
                      >
                        <Phone size={12} />
                        Edit Contact
                      </button>
                    )}
                    <button
                      onClick={unlinkClient}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50 rounded-lg transition-colors"
                      title="Unlink client"
                    >
                      <X size={12} />
                      Unlink
                    </button>
                    <button
                      onClick={openClientPicker}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-forest-700 dark:text-forest-400 bg-forest-50 dark:bg-forest-900/20 hover:bg-forest-100 dark:hover:bg-forest-900/40 rounded-lg transition-colors"
                    >
                      <Plus size={12} strokeWidth={2.5} />
                      Change
                    </button>
                  </>
                ) : (
                  <button
                    onClick={openClientPicker}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-forest-700 dark:text-forest-400 bg-forest-50 dark:bg-forest-900/20 hover:bg-forest-100 dark:hover:bg-forest-900/40 rounded-lg transition-colors"
                  >
                    <Plus size={12} strokeWidth={2.5} />
                    Link Client
                  </button>
                )}
              </div>
            </div>

            {/* Client Picker Dropdown */}
            {showClientPicker && (
              <div className="border border-border rounded-lg bg-secondary/30 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Select client</span>
                  <button onClick={() => setShowClientPicker(false)} className="p-1 hover:bg-secondary rounded">
                    <X size={14} className="text-muted-foreground" />
                  </button>
                </div>
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Search by client name..."
                  className="w-full px-3 py-2 text-xs bg-background border border-border rounded-lg"
                  autoFocus
                />
                <div className="max-h-48 overflow-y-auto space-y-0.5">
                  {loadingClients ? (
                    <p className="text-xs text-muted-foreground text-center py-3">Loading...</p>
                  ) : (() => {
                    const q = clientSearch.toLowerCase();
                    const filtered = availableClients.filter((c) => !q || (c.name || "").toLowerCase().includes(q));
                    return filtered.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-3">
                        {availableClients.length === 0 ? "No clients available" : "No matches"}
                      </p>
                    ) : (
                      filtered.slice(0, 30).map((c) => (
                        <button
                          key={c.id}
                          onClick={() => linkClient(c)}
                          className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-forest-50 dark:hover:bg-forest-900/20 rounded-lg transition-colors text-left"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="font-semibold text-foreground truncate">{c.name}</span>
                            {c.address && (
                              <span className="text-muted-foreground truncate">
                                {c.address}
                              </span>
                            )}
                          </div>
                          <Plus size={12} className="text-forest-600 shrink-0 ml-2" />
                        </button>
                      ))
                    );
                  })()}
                </div>
              </div>
            )}

            <div>
              <span className="text-muted-foreground text-xs uppercase tracking-wide">Client</span>
              <p className="text-sm font-medium text-foreground mt-0.5">
                {workOrder.client_name || <span className="text-muted-foreground/50">Not linked</span>}
              </p>
            </div>

            {workOrder.destination?.address && (
              <div>
                <span className="text-muted-foreground text-xs uppercase tracking-wide">Delivery Address</span>
                <p className="text-sm font-medium text-foreground mt-0.5">
                  {workOrder.destination.address}
                  {workOrder.destination.city && (
                    <>
                      <br />
                      {workOrder.destination.city}, {workOrder.destination.state}{" "}
                      {workOrder.destination.zip}
                    </>
                  )}
                </p>
              </div>
            )}
            {workOrder.preferred_delivery_date && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-muted-foreground text-xs uppercase tracking-wide">Preferred Delivery</span>
                  <p className="text-sm font-medium text-foreground mt-0.5">
                    {formatDate(workOrder.preferred_delivery_date)}
                  </p>
                </div>
                {workOrder.preferred_delivery_time && (
                  <div>
                    <span className="text-muted-foreground text-xs uppercase tracking-wide">Time</span>
                    <p className="text-sm font-medium text-foreground mt-0.5">
                      {workOrder.preferred_delivery_time}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Transportation & delivery — editable, non-intrusive when empty */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Truck size={14} className="text-forest-600" />
                Transportation & delivery
              </h3>
              {!showDeliveryEditor && (
                <button
                  onClick={() => setShowDeliveryEditor(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-forest-700 dark:text-forest-400 bg-forest-50 dark:bg-forest-900/20 hover:bg-forest-100 dark:hover:bg-forest-900/40 rounded-lg transition-colors"
                >
                  <Pencil size={12} />
                  {workOrder.needs_transportation || workOrder.destination?.address || workOrder.preferred_delivery_date ? "Edit" : "Add details"}
                </button>
              )}
            </div>

            {showDeliveryEditor ? (
              <div className="space-y-4 border-t border-border pt-3">
                <label className="flex items-center gap-2 text-sm font-medium text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deliveryForm.needsTransportation}
                    onChange={(e) => setDeliveryForm((f) => ({ ...f, needsTransportation: e.target.checked }))}
                    className="rounded border-border text-forest-600 focus:ring-forest-500"
                  />
                  Requires transportation
                </label>
                {deliveryForm.needsTransportation && (
                  <>
                    <div>
                      <span className="text-muted-foreground text-xs uppercase tracking-wide block mb-1">Address</span>
                      <input
                        type="text"
                        value={deliveryForm.address}
                        onChange={(e) => setDeliveryForm((f) => ({ ...f, address: e.target.value }))}
                        placeholder="Street address"
                        className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-500/50"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <input
                          type="text"
                          value={deliveryForm.city}
                          onChange={(e) => setDeliveryForm((f) => ({ ...f, city: e.target.value }))}
                          placeholder="City"
                          className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-500/50"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          value={deliveryForm.state}
                          onChange={(e) => setDeliveryForm((f) => ({ ...f, state: e.target.value }))}
                          placeholder="State"
                          maxLength={2}
                          className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-500/50 uppercase"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          value={deliveryForm.zip}
                          onChange={(e) => setDeliveryForm((f) => ({ ...f, zip: e.target.value }))}
                          placeholder="ZIP"
                          className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-500/50"
                        />
                      </div>
                    </div>
                  </>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-muted-foreground text-xs uppercase tracking-wide block mb-1">Preferred date</span>
                    <input
                      type="date"
                      value={deliveryForm.preferredDate}
                      onChange={(e) => setDeliveryForm((f) => ({ ...f, preferredDate: e.target.value }))}
                      className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-500/50"
                    />
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs uppercase tracking-wide block mb-1">Preferred time</span>
                    <input
                      type="time"
                      value={deliveryForm.preferredTime}
                      onChange={(e) => setDeliveryForm((f) => ({ ...f, preferredTime: e.target.value }))}
                      className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-500/50"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={saveDelivery}
                    disabled={deliverySaving}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors"
                  >
                    {deliverySaving ? "Saving..." : "Save"}
                  </button>
                  <button
                    onClick={() => {
                      setShowDeliveryEditor(false);
                      if (workOrder) {
                        const dest = workOrder.destination;
                        setDeliveryForm({
                          needsTransportation: !!workOrder.needs_transportation,
                          address: dest?.address || "",
                          city: dest?.city || "",
                          state: dest?.state || "",
                          zip: dest?.zip || "",
                          preferredDate: workOrder.preferred_delivery_date ? workOrder.preferred_delivery_date.slice(0, 10) : "",
                          preferredTime: workOrder.preferred_delivery_time || "",
                        });
                      }
                    }}
                    className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                {!workOrder.needs_transportation && !workOrder.destination?.address && !workOrder.preferred_delivery_date ? (
                  <p className="text-sm text-muted-foreground">
                    No delivery details yet. Add address and preferred date when this order requires transportation.
                  </p>
                ) : (
                  <>
                    {workOrder.needs_transportation && (
                      <div>
                        <span className="text-muted-foreground text-xs uppercase tracking-wide">Destination</span>
                        <p className="text-sm font-medium text-foreground mt-0.5">
                          {workOrder.destination?.address ? (
                            <>
                              {workOrder.destination.address}
                              {(workOrder.destination.city || workOrder.destination.state) && (
                                <><br />{workOrder.destination.city}, {workOrder.destination.state} {workOrder.destination.zip}</>
                              )}
                            </>
                          ) : (
                            <span className="text-muted-foreground/70">Not set</span>
                          )}
                        </p>
                      </div>
                    )}
                    {workOrder.preferred_delivery_date && (
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div>
                          <span className="text-muted-foreground text-xs uppercase tracking-wide">When</span>
                          <p className="text-sm font-medium text-foreground mt-0.5">
                            {formatDate(workOrder.preferred_delivery_date)}
                            {workOrder.preferred_delivery_time ? ` at ${workOrder.preferred_delivery_time}` : ""}
                          </p>
                        </div>
                        {!IS_LOCAL_DEMO && <button
                          type="button"
                          onClick={() => navigate(`/app/ops/calendar${workOrder.preferred_delivery_date ? `?date=${workOrder.preferred_delivery_date.slice(0, 10)}` : ""}`)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-forest-700 dark:text-forest-400 hover:bg-forest-50 dark:hover:bg-forest-900/20 rounded-lg transition-colors"
                        >
                          <Calendar size={12} />
                          View in calendar
                        </button>}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Linked BOLs */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <FileText size={14} className="text-forest-600" />
                Linked BOLs
                {linkedBols.length > 0 && (
                  <span className="text-xs font-medium bg-forest-100 dark:bg-forest-900/30 text-forest-700 dark:text-forest-400 px-2 py-0.5 rounded-full">
                    {linkedBols.length}
                  </span>
                )}
              </h3>
              <button
                onClick={openBolPicker}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-forest-700 dark:text-forest-400 bg-forest-50 dark:bg-forest-900/20 hover:bg-forest-100 dark:hover:bg-forest-900/40 rounded-lg transition-colors"
              >
                <Plus size={12} strokeWidth={2.5} />
                Link BOL
              </button>
            </div>

            {/* BOL Picker Dropdown */}
            {showBolPicker && (
              <div className="border border-border rounded-lg bg-secondary/30 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Select BOL to link</span>
                  <button onClick={() => setShowBolPicker(false)} className="p-1 hover:bg-secondary rounded">
                    <X size={14} className="text-muted-foreground" />
                  </button>
                </div>
                <input
                  type="text"
                  value={bolSearch}
                  onChange={(e) => setBolSearch(e.target.value)}
                  placeholder="Search by BOL #, customer, material..."
                  className="w-full px-3 py-2 text-xs bg-background border border-border rounded-lg"
                  autoFocus
                />
                <div className="max-h-48 overflow-y-auto space-y-0.5">
                  {loadingAvailable ? (
                    <p className="text-xs text-muted-foreground text-center py-3">Loading...</p>
                  ) : (() => {
                    const q = bolSearch.toLowerCase();
                    const filtered = availableBols.filter((b: any) =>
                      !q ||
                      (b.bol_number || "").toLowerCase().includes(q) ||
                      (b.customer_name || "").toLowerCase().includes(q) ||
                      (b.material_type || "").toLowerCase().includes(q)
                    );
                    return filtered.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-3">
                        {availableBols.length === 0 ? "No unlinked BOLs available" : "No matches"}
                      </p>
                    ) : (
                      filtered.slice(0, 30).map((b: any) => (
                        <button
                          key={b.id}
                          onClick={() => linkBol(b.id)}
                          className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-forest-50 dark:hover:bg-forest-900/20 rounded-lg transition-colors text-left"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="font-mono font-semibold text-foreground shrink-0">{b.bol_number}</span>
                            <span className="text-muted-foreground truncate">
                              {b.customer_name || ""}{b.customer_name && b.material_type ? " — " : ""}{b.material_type || ""}
                            </span>
                            {b.work_order_id && (
                              <span className="text-[10px] text-amber-600 dark:text-amber-400 shrink-0">(linked to another WO)</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            <span className="tabular-nums font-semibold text-foreground">{Number(b.weight_tons || b.net_weight_tons || 0).toFixed(2)}t</span>
                            <Plus size={12} className="text-forest-600" />
                          </div>
                        </button>
                      ))
                    );
                  })()}
                </div>
              </div>
            )}

            {loadingBols ? (
              <p className="text-xs text-muted-foreground">Loading...</p>
            ) : linkedBols.length === 0 && !showBolPicker ? (
              <p className="text-xs text-muted-foreground">No BOLs linked to this work order yet.</p>
            ) : linkedBols.length > 0 ? (
              <>
                {/* Progress bar */}
                {(() => {
                  const totalTons = linkedBols.reduce(
                    (s: number, b: any) => s + (Number(b.net_weight_tons) || 0),
                    0
                  );
                  const targetTons = workOrder.quantity && workOrder.quantity_type === "tons" ? workOrder.quantity : 0;
                  const pct = targetTons > 0 ? Math.min(100, (totalTons / targetTons) * 100) : 0;

                  return (
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs">
                        <span className="font-semibold text-foreground tabular-nums">
                          {totalTons.toFixed(2)} tons delivered
                        </span>
                        {targetTons > 0 && (
                          <span className="text-muted-foreground">
                            of {targetTons} tons ({pct.toFixed(0)}%)
                          </span>
                        )}
                      </div>
                      {targetTons > 0 && (
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-forest-600 rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* BOL table */}
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">BOL #</th>
                        <th className="text-left py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Date</th>
                        <th className="text-left py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Material</th>
                        <th className="text-right py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Tons</th>
                        <th className="text-center py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {linkedBols.map((b: any) => (
                        <tr
                          key={b.id}
                          className="border-b border-border/50 hover:bg-secondary/50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/app/ops/bols/${b.id}`)}
                        >
                          <td className="py-2 px-2 font-mono font-semibold text-foreground">{b.bol_number}</td>
                          <td className="py-2 px-2 text-muted-foreground">
                            {b.date ? new Date(b.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "-"}
                          </td>
                          <td className="py-2 px-2 text-muted-foreground truncate max-w-[140px]">{b.material_type || "-"}</td>
                          <td className="py-2 px-2 text-right font-semibold tabular-nums text-foreground">
                            {Number(b.net_weight_tons || 0).toFixed(2)}
                          </td>
                          <td className="py-2 px-2 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              b.status === "delivered"
                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                : "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"
                            }`}>
                              {b.status}
                            </span>
                          </td>
                          <td className="py-2 px-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); unlinkBol(b.id); }}
                              className="p-1 text-muted-foreground/40 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded transition-colors"
                              title="Unlink BOL"
                            >
                              <X size={12} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </div>

          {/* Product info */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Package size={14} className="text-forest-600" />
              Product Information
            </h3>
            {(workOrder.product_illustration_url || workOrder.size_category_illustration_url) && (
              <div className="flex justify-center rounded-lg border border-border bg-muted/30 p-3">
                <img
                  src={workOrder.product_illustration_url || workOrder.size_category_illustration_url || ""}
                  alt={workOrder.product_name || "Product"}
                  className="max-h-32 w-auto max-w-full object-contain rounded"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Product</span>
                <p className="font-medium text-foreground mt-0.5">
                  {workOrder.product_name}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Size</span>
                <p className="font-medium text-foreground mt-0.5">
                  {sizeLabel(workOrder.size_category)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Quantity</span>
                <p className="font-medium text-foreground mt-0.5">
                  {workOrder.quantity}{" "}
                  <span className="text-muted-foreground capitalize">
                    {workOrder.quantity_type || "units"}
                  </span>
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Weight</span>
                <p className="font-medium text-foreground mt-0.5">
                  {formatWeight(workOrder.weight_lbs)}
                </p>
              </div>
              {workOrder.order_type && (
                <div>
                  <span className="text-muted-foreground">Order Type</span>
                  <p className="font-medium text-foreground mt-0.5">
                    {workOrder.order_type}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Line items table (if multi-line) */}
          {hasMultipleLines && (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="p-5 pb-3">
                <h3 className="text-sm font-semibold text-foreground">
                  Line Items ({workOrder.lines!.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-5 py-2.5">#</th>
                      <th className="px-3 py-2.5">Product</th>
                      <th className="px-3 py-2.5 text-center">Illustration</th>
                      <th className="px-3 py-2.5">Size</th>
                      <th className="px-3 py-2.5 text-right">Qty</th>
                      <th className="px-3 py-2.5">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workOrder.lines!.map((line, i) => {
                      const lineImg = line.product_illustration_url || line.size_category_illustration_url;
                      return (
                        <tr
                          key={i}
                          className="border-b border-border/60 last:border-0"
                        >
                          <td className="px-5 py-2.5 text-muted-foreground">
                            {i + 1}
                          </td>
                          <td className="px-3 py-2.5 font-medium text-foreground">
                            {line.productName || (line as any).product_name}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            {lineImg ? (
                              <img
                                src={lineImg}
                                alt={(line.productName || (line as any).product_name) || "Line"}
                                className="h-10 w-10 object-contain rounded border border-border inline-block"
                              />
                            ) : (
                              <span className="text-muted-foreground/50">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-muted-foreground">
                            {sizeLabel(line.sizeCategory || (line as any).size_category)}
                          </td>
                          <td className="px-3 py-2.5 text-right text-foreground tabular-nums">
                            {line.quantity}
                          </td>
                          <td className="px-3 py-2.5 text-muted-foreground capitalize">
                            {line.quantityType || (line as any).quantity_type}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Mixing guidelines */}
          {workOrder.mixing_guidelines && (
            <div className="bg-card border border-border rounded-xl p-5 space-y-2">
              <h3 className="text-sm font-semibold text-foreground">
                Mixing Guidelines
              </h3>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {workOrder.mixing_guidelines}
              </p>
            </div>
          )}

          {/* Attachments */}
          {!IS_LOCAL_DEMO && <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Paperclip size={14} className="text-forest-600" />
                Attachments
                {workOrder.attachments?.length > 0 && (
                  <span className="text-xs text-muted-foreground font-normal">
                    ({workOrder.attachments.length})
                  </span>
                )}
              </h3>
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-forest-600 hover:bg-forest-700 text-white text-xs font-semibold rounded-lg cursor-pointer transition-colors">
                <Upload size={13} />
                {uploading ? "Uploading..." : "Upload"}
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                />
              </label>
            </div>
            {workOrder.attachments?.length > 0 ? (
              <div className="space-y-2">
                {workOrder.attachments.map((att) => (
                  <div
                    key={att.path}
                    className="flex items-center gap-3 p-2.5 bg-secondary/40 rounded-lg group"
                  >
                    {isImageType(att.type) ? (
                      <Image size={16} className="text-blue-500 shrink-0" />
                    ) : (
                      <FileText size={16} className="text-orange-500 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <a
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-foreground hover:text-forest-600 truncate block"
                      >
                        {att.name}
                      </a>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(att.size)}
                      </p>
                    </div>
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 text-muted-foreground hover:text-forest-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Download size={14} />
                    </a>
                    <button
                      onClick={() => handleDeleteAttachment(att.path)}
                      className="p-1.5 text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No files attached. Upload PDFs, images, or documents.</p>
            )}
          </div>}

          {/* Notes — Editable */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <StickyNote size={14} className="text-forest-600" />
                Notes
              </h3>
              {notesSaving && <span className="text-xs text-muted-foreground">Saving...</span>}
              {notesSaved && <span className="text-xs text-green-600">Saved</span>}
            </div>
            <textarea
              value={editableNotes}
              onChange={(e) => {
                setEditableNotes(e.target.value);
                debounceSaveNotes(e.target.value);
              }}
              placeholder="Add notes..."
              rows={6}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500 resize-y"
            />
          </div>
        </div>
      </div>

      {/* Notify Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Send size={18} className="text-amber-600" />
                Send Work Order
              </h2>
              <button
                onClick={() => setShowNotifyModal(false)}
                className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-5">
              {/* Team members */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3">Team Members</h3>
                {loadingRecipients ? (
                  <p className="text-sm text-muted-foreground">Loading team...</p>
                ) : teamRecipients.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No team members found. Add external emails below.</p>
                ) : (
                  <div className="space-y-2">
                    {teamRecipients.map((r) => (
                      <label
                        key={r.id}
                        className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-secondary/50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={r.checked}
                          onChange={(e) =>
                            setTeamRecipients((prev) =>
                              prev.map((m) =>
                                m.id === r.id ? { ...m, checked: e.target.checked } : m
                              )
                            )
                          }
                          className="h-4 w-4 rounded border-border text-forest-600 focus:ring-forest-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{r.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* External emails */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Mail size={14} />
                  Additional Recipients
                </h3>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddExtraEmail())}
                    placeholder="email@example.com"
                    className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                  />
                  <button
                    onClick={handleAddExtraEmail}
                    className="px-3 py-2 bg-forest-600 hover:bg-forest-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <Plus size={16} />
                  </button>
                </div>
                {extraEmails.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {extraEmails.map((email) => (
                      <div
                        key={email}
                        className="flex items-center justify-between px-3 py-1.5 bg-forest-50 dark:bg-forest-900/20 border border-forest-200 dark:border-forest-800 rounded-lg"
                      >
                        <span className="text-sm text-foreground">{email}</span>
                        <button
                          onClick={() => setExtraEmails((prev) => prev.filter((e) => e !== email))}
                          className="p-1 text-muted-foreground hover:text-red-500"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Recipients */}
              {recentRecipients.filter(
                (r) =>
                  !extraEmails.includes(r) &&
                  !teamRecipients.some((t) => t.email.toLowerCase() === r)
              ).length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                    Recent Recipients
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {recentRecipients
                      .filter(
                        (r) =>
                          !extraEmails.includes(r) &&
                          !teamRecipients.some((t) => t.email.toLowerCase() === r)
                      )
                      .map((email) => (
                        <div
                          key={email}
                          className="group flex items-center gap-1 pl-2.5 pr-1 py-1 bg-secondary/60 rounded-full text-xs"
                        >
                          <button
                            onClick={() => {
                              if (!extraEmails.includes(email)) {
                                setExtraEmails((prev) => [...prev, email]);
                              }
                            }}
                            className="text-foreground hover:text-forest-600 truncate max-w-[180px]"
                            title={`Add ${email}`}
                          >
                            {email}
                          </button>
                          <button
                            onClick={() => removeRecentRecipient(email)}
                            className="p-0.5 text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove from recent"
                          >
                            <X size={11} />
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Comment / Notes */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                  <FileText size={14} />
                  Email Comment
                </h3>
                <textarea
                  value={notifyComment}
                  onChange={(e) => setNotifyComment(e.target.value)}
                  placeholder="Add a note to the email notification (optional)..."
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500 resize-none"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-border flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {teamRecipients.filter((r) => r.checked).length + extraEmails.length} recipient(s)
                {workOrder?.attachments?.length ? ` + ${workOrder.attachments.length} file(s)` : ""}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowNotifyModal(false)}
                  className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendNotifications}
                  disabled={notifying || (teamRecipients.filter((r) => r.checked).length + extraEmails.length === 0)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  {notifying ? "Sending..." : (
                    <>
                      <Send size={14} />
                      Send
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inline Client Contact Editor (side drawer) */}
      {showContactEditor && workOrder.client_id && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => {
              if (!contactSaving) {
                setShowContactEditor(false);
                setContactError(null);
              }
            }}
          />
          <div className="relative w-full max-w-md bg-card border-l border-border shadow-2xl max-h-screen overflow-y-auto">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <User size={14} className="text-forest-600" />
                Edit Client Contact
              </h2>
              <button
                onClick={() => {
                  if (!contactSaving) {
                    setShowContactEditor(false);
                    setContactError(null);
                  }
                }}
                className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground"
              >
                <X size={14} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {contactError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-xs text-red-700 dark:text-red-400">
                  {contactError}
                </div>
              )}

              {contactLoading ? (
                <p className="text-xs text-muted-foreground">Loading client...</p>
              ) : (
                <form
                  className="space-y-4"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!workOrder.client_id) return;
                    setContactSaving(true);
                    setContactError(null);
                    try {
                      const payload = {
                        contactName: contactForm.contact_name.trim() || null,
                        contactEmail: contactForm.contact_email.trim() || null,
                        contactPhone: contactForm.contact_phone.trim() || null,
                        address: contactForm.address.trim() || null,
                        notes: contactForm.notes.trim() || null,
                      };
                      const res = await apiFetch(`/api/ops/clients/${workOrder.client_id}`, {
                        method: "PATCH",
                        headers: {
                          Authorization: `Bearer ${token}`,
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify(payload),
                      });
                      if (!res.ok) throw new Error("Failed to save client");
                      setShowContactEditor(false);
                    } catch (err: any) {
                      setContactError(err?.message || "Failed to save client");
                    } finally {
                      setContactSaving(false);
                    }
                  }}
                >
                  <div>
                    <label className="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                      Contact Name
                    </label>
                    <input
                      type="text"
                      value={contactForm.contact_name}
                      onChange={(e) =>
                        setContactForm((prev) => ({ ...prev, contact_name: e.target.value }))
                      }
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                      placeholder="Primary contact person"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                      Contact Email
                    </label>
                    <div className="relative">
                      <Mail
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      />
                      <input
                        type="email"
                        value={contactForm.contact_email}
                        onChange={(e) =>
                          setContactForm((prev) => ({ ...prev, contact_email: e.target.value }))
                        }
                        className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-lg text-sm"
                        placeholder="email@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                      Contact Phone
                    </label>
                    <div className="relative">
                      <Phone
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      />
                      <input
                        type="tel"
                        value={contactForm.contact_phone}
                        onChange={(e) =>
                          setContactForm((prev) => ({ ...prev, contact_phone: e.target.value }))
                        }
                        className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-lg text-sm"
                        placeholder="(602) 555-0000"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                      Address
                    </label>
                    <textarea
                      rows={2}
                      value={contactForm.address}
                      onChange={(e) =>
                        setContactForm((prev) => ({ ...prev, address: e.target.value }))
                      }
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm resize-none"
                      placeholder="Client address"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                      Notes
                    </label>
                    <textarea
                      rows={3}
                      value={contactForm.notes}
                      onChange={(e) =>
                        setContactForm((prev) => ({ ...prev, notes: e.target.value }))
                      }
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm resize-y"
                      placeholder="Internal notes about this client..."
                    />
                  </div>

                  <div className="flex gap-2 pt-1">
                    <button
                      type="submit"
                      disabled={contactSaving}
                      className="flex-1 px-4 py-2.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
                    >
                      {contactSaving ? "Saving..." : "Save Contact"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!contactSaving) {
                          setShowContactEditor(false);
                          setContactError(null);
                        }
                      }}
                      className="px-4 py-2.5 bg-secondary hover:bg-secondary/80 text-sm font-semibold rounded-lg text-foreground transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
